// libs
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

// api
import { likedOrDislikedReview } from 'shared/api/product-details';

//analytics
import NFA from 'shared/helpers/analytics/NFAnalytics';

// helpers
import {
  setReviewToBeLiked,
  getStoredReviewToBeLiked,
  removeReviewToBeLiked,
} from 'shared/components/ReviewCard/Helpful/helpers';
import { getRedirectionURLWithCurrentPage } from 'shared/components/OTPVerification/helpers';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'helpers/newRelic/constants';

// constants
import { ARIA_LABELS } from 'shared/components/ReviewCard/constants';
import { AUTH_URI } from 'shared/constants/uri';
import { STATUS, statusValues, HELPFUL_TEXTS } from './helpers';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';

// hooks
import useKeyboardClick from 'shared/hooks/useKeyboardClick';

// styles
import {
  Container,
  Title,
  IconWrapper,
  Description,
  HelpfulText,
} from 'shared/components/ReviewCard/Helpful/styles';

// types
import {
  IHelpfulProps,
  IReviewMarkedHelpfulApiError,
  IReviewMarkedHelpfulApiResponse,
} from 'shared/components/ReviewCard/Helpful/types';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

const REVIEW_HELPFUL_CLICKED = 'review_helpful_clicked';

function Helpful({
  id,
  productId,
  likeCount,
  isLoggedInUser,
  reviewMarkedHelpful,
  setIsLikedByUser,
}: IHelpfulProps) {
  // Get icon components from unified system
  const ThumbsUp = useStoreIcon(IconName.THUMBS_UP);
  const ThumbsUpFilled = useStoreIcon(IconName.THUMBS_UP, 'filled');

  const [isLiked, setIsLiked] = useState(reviewMarkedHelpful);

  useEffect(() => {
    setIsLiked(reviewMarkedHelpful);
  }, [reviewMarkedHelpful]);

  const history = useHistory();
  const location = useLocation();

  const getHelpfulDescription = () => {
    if (isLiked) {
      return `${HELPFUL_TEXTS.HELPFUL_PREFIX} ${likeCount} ${HELPFUL_TEXTS.HELPFUL_SUFIX}`;
    } else {
      return `${likeCount} ${HELPFUL_TEXTS.HELPFUL_SUFIX}`;
    }
  };

  const redirectToAuth = useCallback(() => {
    const finalUrl = getRedirectionURLWithCurrentPage({
      url: AUTH_URI.LOGIN_OPTIONS,
      search: location.search,
      pathname: location.pathname,
    });

    history.push(finalUrl);
  }, [history, location.pathname, location.search]);

  const onGuestUserClick = useCallback(() => {
    setReviewToBeLiked(String(id));
    redirectToAuth();
  }, [id, redirectToAuth]);

  const onLikedOrDislikedReview = useCallback(() => {
    const action = isLiked ? HELPFUL_TEXTS.REMOVE : HELPFUL_TEXTS.ADD;

    likedOrDislikedReview(productId, String(id), action)
      .then((res: IReviewMarkedHelpfulApiResponse) => {
        if (res.error === false) {
          setIsLiked(!isLiked);
          setIsLikedByUser(id, !isLiked);
        }
      })
      .catch((err: IReviewMarkedHelpfulApiError) => {
        log({
          errTitle: 'Error in helpful review api',
          error: err,
          tags: { [TAG_IDENTIFIER.MODULE]: HELPFUL_TEXTS.HELPFUL_REVIEW },
        });
      });
  }, [id, productId, isLiked, setIsLikedByUser]);

  useEffect(() => {
    if (isLoggedInUser === false) return;

    const reviewToBeLiked = getStoredReviewToBeLiked();

    // there is no review which needs to be liked
    if (Object.keys(reviewToBeLiked).length === 0) {
      return;
    }

    // the review to be liked has not mounted yet
    if (reviewToBeLiked.id !== String(id)) {
      return;
    }

    // remove Review to be liked from session storage
    if (
      reviewToBeLiked.apiCalledStatus === statusValues[STATUS.CALLED] ||
      reviewToBeLiked.apiCalledStatus === statusValues[STATUS.FAILED]
    ) {
      removeReviewToBeLiked();
    }
    // api has been called, update setIsLikedByUser
    if (reviewToBeLiked.apiCalledStatus === statusValues[STATUS.CALLED]) {
      setIsLikedByUser(reviewToBeLiked.id, true);
    }
  }, [id, isLoggedInUser, setIsLikedByUser]);

  const onHelpfulClicked = () => {
    if (isLoggedInUser) onLikedOrDislikedReview();
    else onGuestUserClick();
    NFA.track({
      event: REVIEW_HELPFUL_CLICKED,
      type: TRACK_TYPE.EVENT,
    });
  };

  const keyboardProps = useKeyboardClick(onHelpfulClicked);

  return (
    <Container>
      <Title
        {...keyboardProps}
        aria-label={isLiked ? ARIA_LABELS.REMOVE_HELPFUL : ARIA_LABELS.MARK_HELPFUL}
      >
        <IconWrapper>
          {isLiked ? <ThumbsUpFilled size="16px" /> : <ThumbsUp size="16px" />}
        </IconWrapper>
        <HelpfulText>{HELPFUL_TEXTS.HELPFUL}</HelpfulText>
      </Title>
      {likeCount > 0 && (
        <Description className="user-help-count">{getHelpfulDescription()}</Description>
      )}
    </Container>
  );
}

export default memo(Helpful);
